import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Save, MapPin, Phone, Globe, ExternalLink, Upload, X } from "lucide-react";
import { invalidateUserVenueCache } from "@/lib/pdfVenue";

const API = process.env.REACT_APP_BACKEND_URL;

export default function VenuePage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("");
  const [zip, setZip] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [mapsLink, setMapsLink] = useState("");
  const [notes, setNotes] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [extraLogos, setExtraLogos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadingExtra, setUploadingExtra] = useState(false);
  const fileInputRef = useRef(null);
  const extraFileInputRef = useRef(null);

  const fetchVenue = useCallback(async () => {
    try {
      const v = await api.get("/venue");
      setName(v.name || "");
      setAddress(v.address || "");
      setCity(v.city || "");
      setProvince(v.province || "");
      setZip(v.zip || "");
      setPhone(v.phone || "");
      setEmail(v.email || "");
      setWebsite(v.website || "");
      setMapsLink(v.maps_link || "");
      setLogoUrl(v.logo_url || "");
      setExtraLogos(Array.isArray(v.extra_logos) ? v.extra_logos : []);
      setNotes(v.notes || "");
    } catch (e) {
      toast.error("Errore nel caricamento info circolo");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchVenue(); }, [fetchVenue]);

  const saveVenue = async () => {
    setSaving(true);
    try {
      await api.put("/venue", {
        name, address, city, province, zip, phone, email, website, maps_link: mapsLink, logo_url: logoUrl, extra_logos: extraLogos, notes
      });
      invalidateUserVenueCache();
      toast.success("Info circolo aggiornate");
    } catch (e) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Seleziona un file immagine (JPG, PNG, etc.)");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Il file non deve superare i 5MB");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API}/api/venue/logo`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      if (!res.ok) throw new Error("Upload fallito");
      const data = await res.json();
      setLogoUrl(data.logo_url);
      toast.success("Logo caricato");
    } catch (err) {
      toast.error("Errore nel caricamento del logo");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeLogo = () => {
    setLogoUrl("");
  };

  const handleExtraLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Seleziona un file immagine");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Il file non deve superare i 5MB");
      return;
    }
    setUploadingExtra(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API}/api/venue/extra-logo`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      if (!res.ok) throw new Error("Upload fallito");
      const data = await res.json();
      setExtraLogos((prev) => [...prev, data.logo_url]);
      toast.success("Logo aggiunto");
    } catch (err) {
      toast.error("Errore upload logo aggiuntivo");
    } finally {
      setUploadingExtra(false);
      if (extraFileInputRef.current) extraFileInputRef.current.value = "";
    }
  };

  const removeExtraLogo = async (logoUrlToRemove) => {
    try {
      await api.del("/venue/extra-logo", { logo_url: logoUrlToRemove });
    } catch (err) {
      // Even if server call fails (e.g. logo never persisted), remove locally
    }
    setExtraLogos((prev) => prev.filter((u) => u !== logoUrlToRemove));
    toast.success("Logo rimosso");
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-green-700" /></div>;
  }

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="venue-page">
      <div>
        <p className="text-green-700 text-xs font-bold uppercase tracking-widest mb-1">Il Mio Circolo</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold uppercase tracking-tight text-slate-900">Info Circolo</h1>
      </div>

      {/* Logo Upload */}
      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
            <Upload className="h-5 w-5 text-green-700" /> Logo del Circolo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            {/* Preview */}
            <div className="relative shrink-0">
              {logoUrl ? (
                <div className="relative group">
                  <img src={logoUrl} alt="Logo circolo" className="h-24 w-24 rounded-sm object-contain border border-slate-200 bg-slate-50 p-1" />
                  <button
                    data-testid="venue-remove-logo"
                    onClick={removeLogo}
                    className="absolute -top-2 -right-2 h-6 w-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-md"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <div className="h-24 w-24 rounded-sm border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center">
                  <MapPin className="h-8 w-8 text-slate-300" />
                </div>
              )}
            </div>
            {/* Upload Button */}
            <div className="space-y-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
                id="logo-upload"
              />
              <Button
                data-testid="venue-upload-logo-btn"
                variant="outline"
                size="sm"
                disabled={uploading}
                className="rounded-sm gap-2"
                onClick={() => fileInputRef.current?.click()}
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {logoUrl ? "Cambia Logo" : "Carica Logo"}
              </Button>
              <p className="text-xs text-slate-400">JPG, PNG o SVG. Max 5MB.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Extra Logos (sponsor / federation / partners) */}
      <Card className="bg-white" data-testid="venue-extra-logos-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
            <Upload className="h-5 w-5 text-green-700" /> Loghi Aggiuntivi
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Qui puoi caricare il logo FITP, ATP, WTA o altri loghi (sponsor, federazione, comune, partner). Verranno stampati nei PDF del tabellone e del calendario.
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-3">
            {extraLogos.map((url) => (
              <div key={url} className="relative group">
                <img
                  src={url}
                  alt="Logo aggiuntivo"
                  className="h-20 w-20 rounded-sm object-contain border border-slate-200 bg-slate-50 p-1"
                />
                <button
                  type="button"
                  data-testid={`venue-remove-extra-${url.slice(-12)}`}
                  onClick={() => removeExtraLogo(url)}
                  className="absolute -top-2 -right-2 h-6 w-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-md"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            <div className="h-20 w-20 rounded-sm border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center">
              <input
                ref={extraFileInputRef}
                type="file"
                accept="image/*"
                onChange={handleExtraLogoUpload}
                className="hidden"
                id="extra-logo-upload"
              />
              <button
                type="button"
                data-testid="venue-upload-extra-logo-btn"
                disabled={uploadingExtra}
                className="h-full w-full flex flex-col items-center justify-center gap-1 text-slate-400 hover:text-green-700 transition-colors"
                onClick={() => extraFileInputRef.current?.click()}
              >
                {uploadingExtra ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                  <>
                    <Upload className="h-5 w-5" />
                    <span className="text-[10px] font-bold uppercase tracking-wide">Aggiungi</span>
                  </>
                )}
              </button>
            </div>
          </div>
          {extraLogos.length === 0 && (
            <p className="text-xs text-slate-400 mt-3">
              Nessun logo aggiuntivo. Caricane uno per includerlo nei PDF.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Club Info */}
      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
            <MapPin className="h-5 w-5 text-green-700" /> Dati del Circolo
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wide">Nome Circolo</Label>
            <Input data-testid="venue-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 rounded-sm" placeholder="ASD Tennis Club Roma" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-wide">Indirizzo</Label>
              <Input data-testid="venue-address" value={address} onChange={(e) => setAddress(e.target.value)} className="mt-1 rounded-sm" placeholder="Via dello Sport, 15" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide">CAP</Label>
              <Input data-testid="venue-zip" value={zip} onChange={(e) => setZip(e.target.value)} className="mt-1 rounded-sm" placeholder="00100" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide">Citta</Label>
              <Input data-testid="venue-city" value={city} onChange={(e) => setCity(e.target.value)} className="mt-1 rounded-sm" placeholder="Roma" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide">Provincia</Label>
              <Input data-testid="venue-province" value={province} onChange={(e) => setProvince(e.target.value)} className="mt-1 rounded-sm" placeholder="RM" maxLength={2} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contacts */}
      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
            <Phone className="h-5 w-5 text-green-700" /> Recapiti
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide">Telefono</Label>
              <Input data-testid="venue-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1 rounded-sm" placeholder="+39 06 1234567" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide">Email</Label>
              <Input data-testid="venue-email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 rounded-sm" placeholder="info@tennisclubroma.it" type="email" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide">Sito Web</Label>
              <Input data-testid="venue-website" value={website} onChange={(e) => setWebsite(e.target.value)} className="mt-1 rounded-sm" placeholder="https://www.tennisclubroma.it" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide">Link Google Maps</Label>
              <div className="flex gap-2 mt-1">
                <Input data-testid="venue-maps-link" value={mapsLink} onChange={(e) => setMapsLink(e.target.value)} className="rounded-sm flex-1" placeholder="https://maps.google.com/..." />
                {mapsLink && (
                  <a href={mapsLink} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="icon" className="rounded-sm shrink-0 h-9 w-9" type="button">
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </a>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notes */}
      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="font-heading text-lg font-bold uppercase flex items-center gap-2">
            <Globe className="h-5 w-5 text-green-700" /> Note Aggiuntive
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea data-testid="venue-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-sm min-h-[100px]" placeholder="Informazioni aggiuntive sul circolo..." />
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex justify-end">
        <Button
          data-testid="venue-save-btn"
          onClick={saveVenue}
          disabled={saving}
          className="bg-green-700 hover:bg-green-800 text-white font-bold uppercase tracking-wide rounded-sm gap-2 px-8"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Salva Info Circolo
        </Button>
      </div>
    </div>
  );
}
